import { Form } from "antd";
import { BaseModalRef } from "components/BaseModal";
import message from "components/CustomMessage";
import Vocabulary from "components/Vocabulary";
import { FindingBucketContextType } from "contexts/FindingBucketContext";
import _ from "lodash";
import React, { forwardRef, useCallback, useEffect } from "react";
import FindingAPI from "restAPI/FindingAPI";
import { FindingSummary } from "types/api/findingSummary";
import { ProjectResponseDto } from "types/api/projectResponseDto";
import { FieldTypes } from "utils/formUtils";

import BulkUpdatePopup from "./BulkUpdatePopup";

type Props = {
  afterClose: () => void;
  bucket: FindingBucketContextType; // @TODO - fix ts
};

const ChangeProjectsPopup = forwardRef<BaseModalRef, Props>(
  ({ afterClose, bucket }, ref) => {
    const [form] = Form.useForm();
    const totalFindingsCount = bucket.totalCount;

    const { findings: findingsArray } = bucket;

    const setCurrentProjects = useCallback(() => {
      const { armorcodeProjects } = findingsArray[0];
      const restFindings = _.slice(findingsArray, 1);

      const isEqual = _.every(restFindings, (finding: FindingSummary) =>
        _.isEqual(finding.armorcodeProjects, armorcodeProjects),
      );
      if (isEqual) {
        form.setFieldsValue({
          armorcodeProjects: _.map(armorcodeProjects, (project) => project.id),
        });
      }
    }, [findingsArray, form]);

    useEffect(() => {
      if (findingsArray.length >= 1) {
        setCurrentProjects();
      }
    }, [setCurrentProjects, findingsArray]);

    const handleOnOk = async (data: any) => {
      await bucket.submit(data, FindingAPI.bulk.changeProjects);

      message.success(
        <>
          Changed <Vocabulary name="project" /> for {totalFindingsCount}{" "}
          finding(s). Please check after some time.
        </>,
      );
    };

    const onProjectCreation = useCallback(
      (createProjectData) => {
        const currentValues = form.getFieldsValue(["armorcodeProjects"]);
        form.setFieldsValue({
          armorcodeProjects: [
            createProjectData.id,
            ...(currentValues?.armorcodeProjects || []),
          ],
        });
      },
      [form],
    );

    const fields = [
      {
        name: "armorcodeProjects",
        label: <Vocabulary name="project" />,
        type: FieldTypes.PROJECT_SELECT_ADD_V2,
        props: {
          mode: "multiple",
          apiParams: { filters: {} },
          onCreate: (createdProject: ProjectResponseDto) => {
            onProjectCreation(createdProject);
          },
        },
        rules: [
          {
            required: true,
            message: (
              <>
                Select a <Vocabulary name="project" isLowerCase={true} /> name.
              </>
            ),
          },
        ],
      },
    ];

    const modalProps = {
      okText: (
        <>
          Add to <Vocabulary name="project" isLowerCase={true} />
        </>
      ),
    };

    return (
      <BulkUpdatePopup
        ref={ref}
        title={
          <>
            Add to <Vocabulary name="project" isLowerCase={true} />
            <div className="pull-right text-bold info">
              {totalFindingsCount} findings
            </div>
          </>
        }
        form={form}
        onOk={handleOnOk}
        bucket={bucket}
        fields={fields}
        modalProps={modalProps}
        afterClose={afterClose}
      />
    );
  },
);

export default ChangeProjectsPopup;
